import React, { useContext, useEffect, useState } from "react";
import dayjs from "dayjs";
import { LoadingIcon, TopDropdown } from "../../../../components";
import { getGreenhouses } from "../../../../utils/api";
import { UserContext } from "../../../../context/UserContext";
import { useLocalStorage } from "../../../../hooks";
import { DatePicker } from "antd";

const VisualisationDropdowns = ({
  siteMetadata,
  setSiteMetadata,
  currentOrg,
  setCurrentOrg,
  orgData,
  setOrgData,
  orgLoading,
  setOrgLoading,
  currentSite,
  setCurrentSite,
  siteData,
  setSiteData,
  siteLoading,
  setSiteLoading,
  currentPlot,
  setCurrentPlot,
  currentCrop,
  setCurrentCrop,
  cropData,
  setCropData,
  cropLoading,
  setCropLoading,
  currentGreenhouse,
  setCurrentGreenhouse,
  currentDate,
  setCurrentDate,
  plotData,
  setPlotData,
  greenhouseData,
  setGreenhouseData,
  setDropdownLoading,
  settingDefaults,
  setSettingDefaults,
  searchParams,
  setSearchParams,
  dateData,
  setDateData,
}) => {
  const [defaultDropdown, setDefaultDropdown] = useLocalStorage(
    "dropdowns",
    null,
  );
  const { userData } = useContext(UserContext);

  const [greenHouseLabel, setGreenHouseLabel] = useState("Greenhouse");
  const [blockLabel, setBlockLabel] = useState("Block");
  const [plotLoading, setPlotLoading] = useState(false);
  const [greenhouseLoading, setGreenhouseLoading] = useState(false);

  const getPlotDates = (plot) => (plot?.harvests || []).map((d) => d.date);

  const findPlot = (plotId, greenhouse = currentGreenhouse) =>
    (greenhouse?.plots || plotData || []).find(
      (plot) => plot.id === plotId && (!plot.greenhouse_id || plot.greenhouse_id === greenhouse?.id),
    ) ||
    (greenhouse?.plots || plotData || []).find((plot) => plot.id === plotId);

  const syncPlotSelection = (plot, greenhouse = currentGreenhouse, preferredDate = null) => {
    if (!plot) return;
    const dates = getPlotDates(plot);
    const nextDate = preferredDate && dates.includes(preferredDate) ? preferredDate : dates[0] || null;

    setCurrentPlot(plot);
    setDateData(dates);
    setCurrentDate(nextDate);

    const params = new URLSearchParams(searchParams);
    if (greenhouse) params.set("greenhouse", greenhouse.id);
    params.set("plot", plot.id);
    if (nextDate) params.set("date", nextDate);
    else params.delete("date");
    setSearchParams(params);

    const orgObj = defaultDropdown?.[currentOrg] || {};
    setDefaultDropdown({
      ...defaultDropdown,
      [currentOrg]: {
        ...orgObj,
        greenhouse: greenhouse?.id || orgObj.greenhouse,
        plot: plot.id,
        date: nextDate || orgObj.date,
      },
    });
  };

  const numericFamilyCompare = (aStr, bStr) => {
    const parse = (s) => {
      const str = (s ?? "").toString();
      const match = str.match(/^(\d+)/);
      const num = match ? parseInt(match[1], 10) : Number.POSITIVE_INFINITY;
      const rest = str.slice(match ? match[1].length : 0).toLowerCase();

      // Priority: plain number > "new" > "old" > everything else
      const rank =
        rest === ""
          ? 0
          : rest.includes("new")
            ? 1
            : rest.includes("old")
              ? 2
              : 3;

      return { num, rank, str };
    };

    const A = parse(aStr);
    const B = parse(bStr);

    if (A.num !== B.num) return A.num - B.num;
    if (A.rank !== B.rank) return A.rank - B.rank;
    return A.str.localeCompare(B.str, undefined, {
      numeric: true,
      sensitivity: "base",
    });
  };

  // ------------------------------------------
  // DatePicker disable logic
  // ------------------------------------------
  const disabledDate = (current) => {
    const dayjsDates = (dateData || []).map((date) => dayjs(date));
    return (
      current &&
      !dayjsDates.reduce(
        (enabled, d) => enabled || d.isSame(current, "day"),
        false,
      )
    );
  };

  const handleDateChange = (date, dateString) => {
    if (dateString) {
      setCurrentDate(dateString);
      const params = new URLSearchParams(searchParams);
      params.set("date", dateString);
      setSearchParams(params);
    }
  };

  // ------------------------------------------
  // Fetch greenhouses (ID-based objects) + SORT like NEW
  // ------------------------------------------
  const fetchGreenhouses = async (orgId, siteId, cropId = null) => {
    setGreenhouseLoading(true);
    try {
      const res = await getGreenhouses(orgId, siteId, cropId);
      const greenhouseList = Array.isArray(res?.data?.data)
        ? res.data.data
        : [];
      const raw = greenhouseList.map((greenhouse) => ({
        name: greenhouse.name,
        id: greenhouse.id,
        plots: (greenhouse.plots || []).map((plot) => ({
          ...plot,
          greenhouse_id: greenhouse.id,
          greenhouse_name: greenhouse.name,
        })),
        metadata: greenhouse.metadata,
      }));

      const sorted = [...raw].sort((a, b) =>
        numericFamilyCompare(a.name, b.name),
      );
      setGreenhouseData(sorted);
    } finally {
      setGreenhouseLoading(false);
    }
  };

  // ------------------------------------------
  // On site/crop/org => fetch greenhouses, set dynamic labels
  // ------------------------------------------
  useEffect(() => {
    if (currentSite && currentOrg) {
      const meta = currentSite.metadata?.[currentCrop?.name] || {};
      setSiteMetadata(meta);

      if (meta?.greenhouse_name) setGreenHouseLabel(meta.greenhouse_name);
      if (meta?.block_name) setBlockLabel(meta.block_name);

      fetchGreenhouses(currentOrg, currentSite.id, currentCrop?.id);
    }
  }, [currentSite, currentCrop, currentOrg]);

  // ------------------------------------------
  // Once greenhouseData arrives => pick default greenhouse
  // ------------------------------------------
  useEffect(() => {
    if (greenhouseData?.length) {
      const params = new URLSearchParams(searchParams);
      const greenhouseIdParam = params.get("greenhouse");
      let chosen = null;

      if (greenhouseIdParam) {
        chosen = greenhouseData.find((g) => g.id === greenhouseIdParam) || null;
      } else if (!settingDefaults.greenhouse) {
        const storedGHId = defaultDropdown?.[currentOrg]?.greenhouse; // ID only
        if (storedGHId) {
          chosen = greenhouseData.find((g) => g.id === storedGHId) || null;
        }
        if (!chosen) chosen = greenhouseData[0];
        setSettingDefaults({ ...settingDefaults, greenhouse: true });
      } else {
        chosen = greenhouseData[0];
      }

      if (!chosen) chosen = greenhouseData[0];
      setCurrentGreenhouse(chosen);
      setPlotData(chosen?.plots || []);
    } else {
      setCurrentGreenhouse(null);
      setPlotData([]);
    }
  }, [greenhouseData]);

  // whenever greenhouse changes => set plotData from greenhouse
  useEffect(() => {
    if (currentGreenhouse?.plots) {
      setPlotData(currentGreenhouse.plots);
    } else {
      setPlotData([]);
    }
  }, [currentGreenhouse]);

  // ------------------------------------------
  // On plotData => pick default plot
  // ------------------------------------------
  useEffect(() => {
    if (plotData?.length) {
      const params = new URLSearchParams(searchParams);
      const plotIdParam = params.get("plot");
      const dateParam = params.get("date");
      let chosen = null;

      if (plotIdParam) {
        chosen = findPlot(plotIdParam) || null;
      } else if (!settingDefaults.plot) {
        const storedPlotId = defaultDropdown?.[currentOrg]?.plot;
        if (storedPlotId) {
          chosen = findPlot(storedPlotId) || null;
        }
        if (!chosen) chosen = plotData[0];
        setSettingDefaults({ ...settingDefaults, plot: true });
      } else {
        chosen = plotData[0];
      }

      if (!chosen) chosen = plotData[0];
      syncPlotSelection(
        chosen,
        currentGreenhouse,
        dateParam || defaultDropdown?.[currentOrg]?.date,
      );
    } else {
      setCurrentPlot(null);
      setDateData([]);
    }
  }, [plotData]);

  // ------------------------------------------
  // If currentPlot changes => refresh dateData
  // ------------------------------------------
  useEffect(() => {
    if (currentPlot?.harvests) {
      setDateData(getPlotDates(currentPlot));
    } else {
      setDateData([]);
    }
  }, [currentPlot, setDateData]);

  // ------------------------------------------
  // On dateData => pick default date (URL > first)
  // ------------------------------------------
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    const dateFromUrl = params.get("date");

    if (dateFromUrl && (dateData || []).includes(dateFromUrl)) {
      setCurrentDate(dateFromUrl);
    } else {
      const first = (dateData && dateData[0]) || null;
      setCurrentDate(first);
    }
  }, [dateData, setCurrentDate, searchParams, setSearchParams]);

  // ------------------------------------------
  // Whenever greenhouse changes => update localStorage + param
  // ------------------------------------------
  useEffect(() => {
    if (currentGreenhouse) {
      const orgObj = defaultDropdown?.[currentOrg] || {};
      setDefaultDropdown({
        ...defaultDropdown,
        [currentOrg]: {
          ...orgObj,
          greenhouse: currentGreenhouse.id, // store ID only
        },
      });
    }
  }, [currentGreenhouse]);

  // Whenever plot changes => update localStorage + param
  useEffect(() => {
    if (currentPlot) {
      const orgObj = defaultDropdown?.[currentOrg] || {};
      setDefaultDropdown({
        ...defaultDropdown,
        [currentOrg]: {
          ...orgObj,
          plot: currentPlot.id,
        },
      });
    }
  }, [currentPlot]);

  // ------------------------------------------
  // loading
  // ------------------------------------------
  useEffect(() => {
    setDropdownLoading(
      orgLoading ||
        siteLoading ||
        cropLoading ||
        plotLoading ||
        greenhouseLoading ||
        !currentDate,
    );
  }, [
    orgLoading,
    cropLoading,
    siteLoading,
    plotLoading,
    greenhouseLoading,
    currentDate,
  ]);

  return (
    <>
      {orgLoading || siteLoading || plotLoading || greenhouseLoading ? (
        <LoadingIcon height="100px" />
      ) : (
        <div className="w-full flex flex-wrap gap-4 justify-between sm:flex-col mb-10 dropdowns sm:mb-4">
          {userData?.is_superuser && (
            <TopDropdown
              label="Select Org:"
              val={currentOrg ?? undefined}
              classString="flex-1 min-w-[150px] sm:mb-2"
              placeholder="Select Organization"
              changeCallback={setCurrentOrg}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              filterSort={(a, b) =>
                (a?.label ?? "")
                  .toLowerCase()
                  .localeCompare((b?.label ?? "").toLowerCase())
              }
              notFound={orgLoading ? <LoadingIcon /> : null}
              data={orgData.map((org) => ({
                value: org.id,
                label: org.name,
              }))}
            />
          )}

          <TopDropdown
            label="Select Site:"
            val={currentSite?.id ?? undefined}
            classString="flex-1 min-w-[150px] sm:mb-2"
            placeholder="Select Site"
            changeCallback={(siteId) => {
              const siteObj = siteData.find((s) => s.id === siteId);
              setCurrentSite(siteObj || null);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(a, b) =>
              (a?.label ?? "")
                .toLowerCase()
                .localeCompare((b?.label ?? "").toLowerCase())
            }
            notFound={siteLoading ? <LoadingIcon /> : null}
            data={siteData?.map((site) => ({
              value: site.id,
              label: site.displayName,
            }))}
          />

          {/* Crop => ID-based */}
          <TopDropdown
            label="Select Crop:"
            val={currentCrop?.id ?? undefined}
            placeholder="Select Crop"
            classString="flex-1 min-w-[150px] sm:mb-2"
            changeCallback={(cropId) => {
              const cropObj = cropData.find((c) => c.id === cropId);
              setCurrentCrop(cropObj || null);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(a, b) =>
              (a?.label ?? "")
                .toLowerCase()
                .localeCompare((b?.label ?? "").toLowerCase())
            }
            data={cropData?.map((crop) => ({
              value: crop.id,
              label: crop.displayName,
            }))}
          />

          {/* Greenhouse => numeric-aware sort like NEW */}
          <TopDropdown
            label={`Select ${greenHouseLabel}:`}
            val={currentGreenhouse?.id ?? undefined}
            placeholder={`Select ${greenHouseLabel}`}
            classString="flex-1 min-w-[150px] sm:mb-2"
            changeCallback={(ghId) => {
              const ghObj = greenhouseData.find((gh) => gh.id === ghId);
              setCurrentGreenhouse(ghObj || null);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            data={(() => {
              const list = greenhouseData || [];
              const sorted = [...list].sort((a, b) =>
                numericFamilyCompare(a.name, b.name),
              );
              return sorted.map((gh) => ({ value: gh.id, label: gh.name }));
            })()}
          />

          {/* Plot => numeric-aware sort like NEW */}
          <TopDropdown
            label={`Select ${blockLabel}:`}
            val={
              currentGreenhouse && currentPlot
                ? `${currentGreenhouse.id}---${currentPlot.id}`
                : undefined
            }
            placeholder={`Select ${blockLabel}`}
            classString="flex-1 min-w-[150px] sm:mb-2"
            changeCallback={(value) => {
              const [greenhouseId, plotId] = value.split("---");
              const greenhouse = greenhouseData.find((gh) => gh.id === greenhouseId);
              syncPlotSelection(findPlot(plotId, greenhouse), greenhouse);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            data={(() => {
              const list = plotData || [];
              const sorted = [...list].sort((a, b) =>
                numericFamilyCompare(a.name, b.name),
              );
              return sorted.map((p) => ({
                value: `${p.greenhouse_id || currentGreenhouse?.id}---${p.id}`,
                label: p.name,
              }));
            })()}
          />

          {/* DatePicker => single date */}
          <div className="flex flex-col">
            <div className="text-sm text-darkerGray max-w-full mb-1">
              Select Date:
            </div>
            <DatePicker
              value={currentDate ? dayjs(currentDate) : null}
              className="flex-1 min-w-[150px] sm:mb-2"
              disabledDate={disabledDate}
              onChange={handleDateChange}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default VisualisationDropdowns;
